import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { AppThunk } from '../../../app/store';
import { getDraftEndPoint } from '../../../common/api.endPoints';
import { HttpService } from '../../../common/http.service';

interface DraftSnapshot {
  content: string;
  mibCodes: string[];
  lastSavedAt: string;
  authorName: string;
  expiresAt: string | null;
}

interface NotesDraftState {
  isOpen: boolean;
  loadedApplicationId: string | null;
  lastSavedSnapshot: string;
  lastSavedMibCodes: string[];
  lastSavedAt: string | null;
  authorName: string | null;
  expiresAt: string | null;

  isLoading: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  saveError: string | null;
}

const initialState: NotesDraftState = {
  isOpen: false,
  loadedApplicationId: null,
  lastSavedSnapshot: '',
  lastSavedMibCodes: [],
  lastSavedAt: null,
  authorName: null,
  expiresAt: null,
  isLoading: false,
  isSaving: false,
  isDeleting: false,
  saveError: null,
};

export const notesDraftSlice = createSlice({
  name: 'notesDraft',
  initialState,
  reducers: {
    openDrawer: state => {
      state.isOpen = true;
    },
    closeDrawer: state => {
      state.isOpen = false;
    },
    toggleDrawer: state => {
      state.isOpen = !state.isOpen;
    },
    loadStart: (state, action: PayloadAction<{ applicationId: string }>) => {
      state.isLoading = true;
      state.loadedApplicationId = action.payload.applicationId;
    },
    loadSuccess: (state, action: PayloadAction<DraftSnapshot>) => {
      state.isLoading = false;
      state.lastSavedSnapshot = action.payload.content;
      state.lastSavedMibCodes = action.payload.mibCodes;
      state.lastSavedAt = action.payload.lastSavedAt;
      state.authorName = action.payload.authorName;
      state.expiresAt = action.payload.expiresAt;
    },
    loadEmpty: state => {
      state.isLoading = false;
      state.lastSavedSnapshot = '';
      state.lastSavedMibCodes = [];
      state.lastSavedAt = null;
      state.authorName = null;
      state.expiresAt = null;
    },
    saveStart: state => {
      state.isSaving = true;
      state.saveError = null;
    },
    saveSuccess: (state, action: PayloadAction<DraftSnapshot>) => {
      state.isSaving = false;
      state.saveError = null;
      state.lastSavedSnapshot = action.payload.content;
      state.lastSavedMibCodes = action.payload.mibCodes;
      state.lastSavedAt = action.payload.lastSavedAt;
      state.authorName = action.payload.authorName;
      state.expiresAt = action.payload.expiresAt;
    },
    saveFailure: (state, action: PayloadAction<{ message: string }>) => {
      state.isSaving = false;
      state.saveError = action.payload.message;
    },
    deleteStart: state => {
      state.isDeleting = true;
      state.saveError = null;
    },
    deleteSuccess: state => {
      state.isDeleting = false;
      state.saveError = null;
      state.lastSavedSnapshot = '';
      state.lastSavedAt = null;
      state.authorName = null;
      state.expiresAt = null;
    },
    deleteFailure: (state, action: PayloadAction<{ message: string }>) => {
      state.isDeleting = false;
      state.saveError = action.payload.message;
    },
    resetDraft: () => initialState,
  },
});

export const notesDraftActions = notesDraftSlice.actions;
export const notesDraftReducer = notesDraftSlice.reducer;

const toSnapshot = (body: any): DraftSnapshot => ({
  content: body.content ?? '',
  mibCodes: Array.isArray(body.mibCodes) ? body.mibCodes : [],
  lastSavedAt: body.updatedAt ?? new Date().toISOString(),
  authorName: body.authorName ?? '',
  expiresAt: body.expiresAt ?? null,
});

export const loadDraft =
  (applicationId: string): AppThunk =>
  async dispatch => {
    dispatch(notesDraftActions.loadStart({ applicationId }));
    const response = await HttpService.getInstance().get(
      getDraftEndPoint(applicationId),
    );

    if (response?.error) {
      dispatch(notesDraftActions.loadEmpty());
      return;
    }

    dispatch(notesDraftActions.loadSuccess(toSnapshot(response.parsedBody)));
  };

export const saveDraft =
  (
    applicationId: string,
    content: string,
    mibCodes: string[] = [],
  ): AppThunk<Promise<boolean>> =>
  async dispatch => {
    dispatch(notesDraftActions.saveStart());
    const response = await HttpService.getInstance().put(
      getDraftEndPoint(applicationId),
      { content, mibCodes },
    );

    if (response?.error) {
      dispatch(
        notesDraftActions.saveFailure({
          message:
            response.error?.message ??
            'Draft not saved — check your connection.',
        }),
      );
      return false;
    }

    dispatch(notesDraftActions.saveSuccess(toSnapshot(response.parsedBody)));
    return true;
  };

export const deleteDraft =
  (applicationId: string): AppThunk<Promise<boolean>> =>
  async dispatch => {
    dispatch(notesDraftActions.deleteStart());
    const response = await HttpService.getInstance().delete(
      getDraftEndPoint(applicationId),
    );

    if (response?.error) {
      dispatch(
        notesDraftActions.deleteFailure({
          message:
            response.error?.message ??
            'Draft not deleted — check your connection.',
        }),
      );
      return false;
    }

    dispatch(notesDraftActions.deleteSuccess());
    return true;
  };
